/**
 * App — top-level providers + routes (P26).
 *
 * 15 sections from the wireframe map to 15 React Router routes. Home /
 * Command Center renders the lifted dashboard; the other 14 render
 * `<SectionStub>` until P27/P28/P29 lift their sections from
 * `sample-wireframe/client/src/pages/Home.tsx`.
 *
 * `wouter` from the wireframe is replaced with `react-router-dom@7` per
 * P26 phase-doc decision (better nesting + outlets for layouts).
 */
import type { JSX } from 'react';
import { Route, Routes } from 'react-router-dom';

import { ConsoleLayout } from '@/components/console/Layout.js';
import { ThemeProvider } from '@/contexts/ThemeContext.js';
import AccessMatrix from '@/routes/AccessMatrix.js';
import AccessRequest from '@/routes/AccessRequest.js';
import AdminSettings from '@/routes/AdminSettings.js';
import Approvals from '@/routes/Approvals.js';
import AuditExplorer from '@/routes/AuditExplorer.js';
import Catalog from '@/routes/Catalog.js';
import ComposioToolkits from '@/routes/ComposioToolkits.js';
import Connectors from '@/routes/Connectors.js';
import Home from '@/routes/Home.js';
import MyAccess from '@/routes/MyAccess.js';
import Observability from '@/routes/Observability.js';
import PolicyStudio from '@/routes/PolicyStudio.js';
import Proxy from '@/routes/Proxy.js';
import PublisherWorkspace from '@/routes/PublisherWorkspace.js';
import Register from '@/routes/Register.js';
import WrapperCredentials from '@/routes/WrapperCredentials.js';
import WrapperEditor from '@/routes/WrapperEditor.js';
import RuntimeHealth from '@/routes/RuntimeHealth.js';
import ServerDetail from '@/routes/ServerDetail.js';
import ServerAccess from '@/routes/ServerAccess.js';
import Sessions from '@/routes/Sessions.js';
// SectionStub kept in `routes/SectionStub.tsx` as a future fallback /
// 404 handler; no longer referenced by App since all 15 sections are
// lifted (P26 + P27 + P28 + P29).
import ToolkitDetail from '@/routes/ToolkitDetail.js';
import Triggers from '@/routes/Triggers.js';
import UsageAnalytics from '@/routes/UsageAnalytics.js';
import NotFound from '@/routes/NotFound.js';
import { TooltipProvider } from '@/components/ui/tooltip.js';
import { Toaster } from '@/components/ui/sonner.js';

export function App(): JSX.Element {
  return (
    <ThemeProvider defaultTheme="light">
      <TooltipProvider>
        <Toaster />
        <Routes>
          <Route element={<ConsoleLayout />}>
            <Route path="/" element={<Home />} />

            {/* Discover (P27) */}
            <Route path="/catalog" element={<Catalog />} />
            <Route path="/catalog/toolkits/:slug" element={<ToolkitDetail />} />
            <Route path="/connectors" element={<Connectors />} />
            <Route path="/govern/composio-toolkits" element={<ComposioToolkits />} />

            {/* Build (P28 + P30 publisher workspace) */}
            <Route path="/build/register" element={<Register />} />
            <Route path="/build/sessions" element={<Sessions />} />
            <Route path="/build/proxy" element={<Proxy />} />
            <Route path="/build/publisher" element={<PublisherWorkspace />} />
            <Route path="/build/servers/:id" element={<ServerDetail />} />
            <Route path="/build/servers/:id/access" element={<ServerAccess />} />
            <Route path="/build/wrappers/new" element={<WrapperEditor />} />
            <Route path="/build/wrappers/:slug/edit" element={<WrapperEditor />} />
            <Route path="/build/wrappers/:slug/credentials" element={<WrapperCredentials />} />

            {/* Automate (P28) */}
            <Route path="/automate/triggers" element={<Triggers />} />

            {/* Govern (P29) */}
            <Route path="/govern/approvals" element={<Approvals />} />
            <Route path="/govern/policy" element={<PolicyStudio />} />
            <Route path="/govern/audit" element={<AuditExplorer />} />

            {/* Operate (P29) */}
            <Route path="/operate/usage" element={<UsageAnalytics />} />
            <Route path="/operate/runtime" element={<RuntimeHealth />} />
            <Route path="/operate/observability" element={<Observability />} />

            {/* Admin (P29) */}
            <Route path="/admin/settings" element={<AdminSettings />} />

            {/* Admin — direct deeplinks to each tab (Phase 2.A.6) */}
            <Route path="/admin/workgroups" element={<AdminSettings defaultTab="Workgroups" />} />
            <Route path="/admin/clients" element={<AdminSettings defaultTab="OAuth Clients" />} />
            <Route
              path="/admin/virtual-servers"
              element={<AdminSettings defaultTab="Virtual Servers" />}
            />

            {/* Access (Round 1.4 — A5.4) */}
            <Route path="/access" element={<MyAccess />} />
            <Route path="/access/request" element={<AccessRequest />} />

            {/* Admin access matrix (Phase 2.A.5) */}
            <Route path="/admin/access" element={<AccessMatrix />} />

            {/* Catch-all 404 — must stay last inside the layout. */}
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </TooltipProvider>
    </ThemeProvider>
  );
}
